import { useCallback, useEffect, useLayoutEffect, useRef, type MutableRefObject, type RefObject } from 'react';

import {
    CHART_TO_TABLE_POST_PAGINATION_SYNC_RETRY_MS,
    HORIZONTAL_PAGINATION_RIGHT_EDGE_THRESHOLD_PX,
    setPackArchitectureTableScrollLeft,
    TABLE_PAGINATION_SCROLL_RETREAT_PX,
} from './packArchitectureTable.helpers';

type ScrollSnapshot = {
    scrollLeft: number;
    scrollWidth: number;
    columnCountBeforeFetch: number;
};

type UsePackArchitectureTableScrollParams = {
    tableWrapperRef: RefObject<HTMLDivElement | null>;
    builtTableRowCount: number;
    visibleColumnCount: number;
    loadedColumnCount: number;
    tableHasMorePages: boolean;
    isChartLoading: boolean;
    isTablePaginatedLoading: boolean;
    isChartPaginatedLoading: boolean;
    fetchNextTablePage: () => Promise<void>;
    activeSegmentKey: string | null;
    selectedColumnKey: string | null;
    isSyncingTableFromChartRef?: MutableRefObject<boolean>;
    suppressChartToTableSyncRef?: MutableRefObject<boolean>;
};

const PAGINATION_SCROLL_RETRY_MS = [0, 50, 150];
const TABLE_PAGINATION_SUPPRESS_CHART_SYNC_CLEAR_MS =
    Math.max(...PAGINATION_SCROLL_RETRY_MS, ...CHART_TO_TABLE_POST_PAGINATION_SYNC_RETRY_MS) + 50;

export function usePackArchitectureTableScroll({
    tableWrapperRef,
    builtTableRowCount,
    visibleColumnCount,
    loadedColumnCount,
    tableHasMorePages,
    isChartLoading,
    isTablePaginatedLoading,
    isChartPaginatedLoading,
    fetchNextTablePage,
    activeSegmentKey,
    selectedColumnKey,
    isSyncingTableFromChartRef,
    suppressChartToTableSyncRef,
}: UsePackArchitectureTableScrollParams) {
    const isFetchingNextPageRef = useRef(false);
    const isRestoringScrollRef = useRef(false);
    const scrollSnapshotRef = useRef<ScrollSnapshot | null>(null);
    const suppressChartSyncClearTimeoutRef = useRef<number | null>(null);

    const restoreScrollAfterPagination = useCallback(
        (tableWrapper: HTMLElement, snapshot: ScrollSnapshot) => {
            const tableBody = tableWrapper.querySelector('.ant-table-body') as HTMLElement | null;
            if (!tableBody) return;

            const widthGrowth = tableBody.scrollWidth - snapshot.scrollWidth;
            const targetScrollLeft =
                widthGrowth > 0
                    ? snapshot.scrollLeft + widthGrowth - TABLE_PAGINATION_SCROLL_RETREAT_PX
                    : snapshot.scrollLeft - TABLE_PAGINATION_SCROLL_RETREAT_PX;

            isRestoringScrollRef.current = true;
            setPackArchitectureTableScrollLeft(tableWrapper, Math.max(0, targetScrollLeft));
            requestAnimationFrame(() => {
                isRestoringScrollRef.current = false;
            });
        },
        [],
    );

    const handleTableScroll = useCallback(
        async (event: Event) => {
            if (isRestoringScrollRef.current || isSyncingTableFromChartRef?.current) return;

            const target = event.target as HTMLElement;
            const reachedRightEdge =
                target.scrollLeft + target.clientWidth >=
                target.scrollWidth - HORIZONTAL_PAGINATION_RIGHT_EDGE_THRESHOLD_PX;

            if (
                !reachedRightEdge ||
                !tableHasMorePages ||
                isChartLoading ||
                isChartPaginatedLoading ||
                isTablePaginatedLoading ||
                isFetchingNextPageRef.current
            ) {
                return;
            }

            scrollSnapshotRef.current = {
                scrollLeft: target.scrollLeft,
                scrollWidth: target.scrollWidth,
                columnCountBeforeFetch: loadedColumnCount,
            };
            if (suppressChartToTableSyncRef) {
                suppressChartToTableSyncRef.current = true;
            }
            isFetchingNextPageRef.current = true;

            try {
                await fetchNextTablePage();
            } finally {
                isFetchingNextPageRef.current = false;
            }
        },
        [
            fetchNextTablePage,
            isChartLoading,
            isChartPaginatedLoading,
            isTablePaginatedLoading,
            isSyncingTableFromChartRef,
            loadedColumnCount,
            suppressChartToTableSyncRef,
            tableHasMorePages,
        ],
    );

    useEffect(() => {
        const tableBody = tableWrapperRef.current?.querySelector('.ant-table-body');
        if (!tableBody) return undefined;

        tableBody.addEventListener('scroll', handleTableScroll);
        return () => tableBody.removeEventListener('scroll', handleTableScroll);
    }, [handleTableScroll, builtTableRowCount, visibleColumnCount, tableWrapperRef]);

    useLayoutEffect(() => {
        const snapshot = scrollSnapshotRef.current;
        const tableWrapper = tableWrapperRef.current;

        if (
            !snapshot ||
            !tableWrapper ||
            isTablePaginatedLoading ||
            loadedColumnCount <= snapshot.columnCountBeforeFetch
        ) {
            return;
        }

        restoreScrollAfterPagination(tableWrapper, snapshot);
    }, [isTablePaginatedLoading, loadedColumnCount, restoreScrollAfterPagination, tableWrapperRef]);

    useEffect(() => {
        const snapshot = scrollSnapshotRef.current;
        const tableWrapper = tableWrapperRef.current;

        if (
            !snapshot ||
            !tableWrapper ||
            isTablePaginatedLoading ||
            loadedColumnCount <= snapshot.columnCountBeforeFetch
        ) {
            return;
        }

        const timeoutIds = PAGINATION_SCROLL_RETRY_MS.map((delayMs) =>
            window.setTimeout(() => {
                restoreScrollAfterPagination(tableWrapper, snapshot);
            }, delayMs),
        );

        if (suppressChartToTableSyncRef) {
            if (suppressChartSyncClearTimeoutRef.current !== null) {
                window.clearTimeout(suppressChartSyncClearTimeoutRef.current);
            }
            suppressChartSyncClearTimeoutRef.current = window.setTimeout(() => {
                suppressChartToTableSyncRef.current = false;
                suppressChartSyncClearTimeoutRef.current = null;
            }, TABLE_PAGINATION_SUPPRESS_CHART_SYNC_CLEAR_MS);
        }

        scrollSnapshotRef.current = null;

        return () => {
            timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
            if (suppressChartSyncClearTimeoutRef.current !== null) {
                window.clearTimeout(suppressChartSyncClearTimeoutRef.current);
                suppressChartSyncClearTimeoutRef.current = null;
            }
        };
    }, [
        isTablePaginatedLoading,
        loadedColumnCount,
        restoreScrollAfterPagination,
        suppressChartToTableSyncRef,
        tableWrapperRef,
        visibleColumnCount,
    ]);

    useEffect(() => {
        if (!activeSegmentKey || isTablePaginatedLoading) return;

        const tableBody = tableWrapperRef.current?.querySelector('.ant-table-body');
        if (!tableBody) return;

        const selectedRow = tableBody.querySelector(`tr[data-row-key="${CSS.escape(activeSegmentKey)}"]`);
        selectedRow?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

        if (selectedColumnKey) {
            const selectedCell = tableBody.querySelector(
                `tr[data-row-key="${CSS.escape(activeSegmentKey)}"] td.vendor-cell-selected`,
            );
            selectedCell?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        }
    }, [activeSegmentKey, isTablePaginatedLoading, selectedColumnKey, builtTableRowCount, tableWrapperRef]);
}
